import { useSearchParams } from 'next/navigation';

import { appConfig } from '@/config/appConfig';
import useGet from '@/hooks/useGet';
import type { ErrorResponse } from '@/types/ErrorResponse';
import type { Meta } from '@/types/Meta';
import type { PaginationResponse } from '@/types/PaginationResponse';

import { filterParams } from '../constants/filterParams';
import { fleetSizesRoutes } from '../routes/fleetSizesRoutes';
import type { FleetSize } from '../types/FleetSize';

type UseFetchFleetSizesParams = {
    page?: number;
    perPage?: number;
    sortBy?: string;
    orderBy?: string;
    search?: string;
};

type UseFetchFleetSizesReturn = {
    fleetSizes: FleetSize[];
    meta: Meta | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
};

export default function useFetchFleetSizes({
    page,
    perPage,
    sortBy,
    orderBy,
    search,
}: UseFetchFleetSizesParams = {}): UseFetchFleetSizesReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // -----------------------------------------------------------------------------------------------
    const searchParams = useSearchParams();

    const pageNumber = page ?? Number(searchParams.get(filterParams.page.key) ?? appConfig.filter.defaultPage);
    const perPageNumber = perPage ?? Number(searchParams.get(filterParams.perPage.key) ?? appConfig.filter.defaultPerPage);
    const sortByQuery = sortBy ?? searchParams.get(filterParams.sortBy.key) ?? appConfig.filter.defaultSortBy;
    const orderByQuery = orderBy ?? searchParams.get(filterParams.orderBy.key) ?? appConfig.filter.defaultOrderBy;
    const searchQuery = search ?? searchParams.get(filterParams.search.key) ?? appConfig.filter.defaultSearch;

    const queryParams = {
        [appConfig.filter.defaultPageQueryName]: pageNumber,
        [appConfig.filter.defaultPerPageQueryName]: perPageNumber,
        [appConfig.filter.defaultSortByQueryName]: sortByQuery,
        [appConfig.filter.defaultOrderByQueryName]: orderByQuery,
        [appConfig.filter.defaultSearchQueryName]: searchQuery,
    };

    const { response, error, isLoading, isFetching, isError } = useGet<PaginationResponse<FleetSize>, ErrorResponse>(
        fleetSizesRoutes.api.base(),
        {
            queryParams,
        }
    );

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        fleetSizes: response?.data ?? [],
        meta: response?.meta ?? null,
        error,
        isLoading,
        isFetching,
        isError,
    };
}
